// src/npm-shim.ts
//
// Read the program out of an npm-generated launcher script, so `clodex patch`
// operates on the real Claude Code executable instead of the two-kilobyte
// script that starts it.
//
// **This is a Windows install shape.** npm's `bin-links` writes a symlink for a
// package `bin` on POSIX and only calls `cmd-shim` on Windows, where it writes
// THREE launchers side by side: an extensionless `sh` script, a `.cmd` batch
// file, and a `.ps1` PowerShell script. Each names its program RELATIVE to its
// own directory, and none of them is a Claude Code binary — tweakcc refuses them
// with "Unable to detect installation type from path ...". Windows has no
// symlink for the PATH lookup to follow, so `realpathSync` cannot see through
// them and the file has to be parsed (issue #193). The parsing itself is
// platform-independent, which is what makes it testable off Windows; a POSIX
// user who has such a launcher (a Windows bin directory on a share, another tool
// that writes cmd-shim output) gets the same treatment.
//
// The three grammars are ported from `read-cmd-shim` 6.0.0 (`lib/index.js`, ISC
// licence), the reader bundled with npm 11.11.0 in Node 24.14.1. They produce
// the same captures as that reader on the launchers `cmd-shim` 8.0.0 writes for
// both bin shapes Claude Code has shipped: a native `bin/claude.exe` (the
// launcher runs it directly) and a legacy `cli.js` (the launcher finds a nearby
// node and passes the script to it — the program named last, before the argument
// forwarder, is the one that matters). `cmd-shim` 9.0.2, which npm 12 resolves
// to, renames the sh launcher's base directory to `$basedir_win` for the legacy
// shape; both spellings are accepted. Every fixture in
// `tests/helpers/npm-launchers.ts` was generated by running those real packages.
// read-cmd-shim is not in this package's dependency graph and
// `pnpm-workspace.yaml`'s `minimumReleaseAge` gate makes taking a new dependency
// for twenty lines of regex a poor trade, so it is reimplemented here.
//
// Two things this does that read-cmd-shim does not, because clodex uses the
// answer to pick a file to OVERWRITE rather than to report on a link:
// FULL-LINE comments (`REM`, `::`, `#`) are ignored, and a launcher whose
// remaining lines name two different programs is refused instead of resolved to
// whichever came first. Neither is shell analysis — a line that a shell would
// never reach (a dead `if` branch, a heredoc body, an inline trailing comment)
// still counts as a candidate, and the result there is the refusal, not a
// guess.

import { readFileSync, realpathSync, statSync } from 'node:fs';
import { dirname, resolve } from 'node:path';

/**
 * Largest file clodex will read looking for a launcher. cmd-shim's scripts are
 * about a kilobyte; a native Claude Code binary is ~200 MB and an npm `cli.js`
 * is megabytes, so this both keeps the check cheap and stops it from slurping
 * the very thing it is trying to find. A file over the limit is the program, not
 * a launcher — whatever launcher-shaped text it may happen to contain.
 */
const MAX_LAUNCHER_BYTES = 64 * 1024;

/**
 * How many launchers deep to follow. npm never chains them, so anything past
 * the first hop is already unusual; the cap only guarantees a hand-made cycle
 * terminates. Four launchers are followed and the fifth file is classified.
 */
const MAX_LAUNCHER_HOPS = 4;

type LauncherKind = 'cmd' | 'powershell' | 'sh';

/**
 * A line clodex must not read a program out of. Batch comments are `REM` or
 * `::`; `sh` and PowerShell both use `#`. A single leading `:` is a batch LABEL
 * (`:start`), not a comment, so it is deliberately not matched.
 *
 * This is a lexical test on whole lines only. It does not know which lines a
 * shell would actually execute; anything it does not strike out is a candidate,
 * and two disagreeing candidates end in a refusal rather than a choice.
 */
const COMMENT_LINE: Record<LauncherKind, RegExp> = {
  cmd: /^[\s@]*(?:rem\b|::)/i,
  powershell: /^\s*#/,
  sh: /^\s*#/,
};

/**
 * `"%dp0%\rel\path" %*` — batch (`%~dp0` in older cmd-shim);
 * `& "$basedir/rel/path" $args` — PowerShell;
 * `exec "$basedir/rel/path" "$@"` — sh (`$basedir_win` since cmd-shim 9).
 *
 * Applied per LINE, never across the whole file: `[^"]+?` would otherwise span
 * newlines and stitch a target out of two unrelated lines.
 */
const TARGET_PATTERN: Record<LauncherKind, RegExp> = {
  cmd: /"%(?:~dp0|dp0%)\\([^"]+?)"\s+%\*/g,
  powershell: /"\$basedir\/([^"]+?)"\s+\$args/g,
  sh: /"\$basedir(?:_win)?\/([^"]+?)"\s+"\$@"/g,
};

function launcherKind(shimPath: string): LauncherKind {
  const lower = shimPath.toLowerCase();
  if (lower.endsWith('.cmd')) return 'cmd';
  if (lower.endsWith('.ps1')) return 'powershell';
  return 'sh';
}

function readLauncherText(path: string): string | null {
  try {
    if (statSync(path).size > MAX_LAUNCHER_BYTES) return null;
    return readFileSync(path, 'utf8');
  } catch {
    return null;
  }
}

/**
 * A capture still holding `%VAR%` or `$var` was not fully expanded by the
 * grammar above, so clodex does not know what file it names. Guessing is the
 * one thing the patcher must not do — the path it settles on is what gets
 * backed up, patched and renamed over.
 */
function isLiteralPath(relativeTarget: string): boolean {
  return relativeTarget.length > 0 && !/[%$]/.test(relativeTarget);
}

/** Every program named by a line that is not a whole-line comment, in file order. */
function collectTargets(contents: string, kind: LauncherKind): string[] {
  const comment = COMMENT_LINE[kind];
  const pattern = TARGET_PATTERN[kind];
  const targets: string[] = [];
  for (const line of contents.split(/\r?\n/)) {
    if (comment.test(line)) continue;
    for (const match of line.matchAll(pattern)) targets.push(match[1] as string);
  }
  return targets;
}

export type NpmShimReading =
  /** Not an npm launcher — patch this file itself. */
  | { kind: 'not-a-shim' }
  /** A Windows launcher whose program clodex could not read. Never patchable. */
  | { kind: 'unreadable-launcher' }
  /** A launcher naming more than one program. Which one runs is not knowable here. */
  | { kind: 'ambiguous-launcher'; targets: string[] }
  /** An npm launcher; `target` is the absolute path it starts. */
  | { kind: 'target'; target: string };

/**
 * Classify one file. `target` is resolved against the launcher's OWN directory,
 * which is how every cmd-shim launcher addresses its program (`%dp0%`,
 * `$basedir` and `$basedir_win` are all "the directory this script is in"), so a
 * relocated or junctioned install still resolves correctly.
 */
export function readNpmShim(shimPath: string): NpmShimReading {
  const kind = launcherKind(shimPath);
  // A `.cmd` or `.ps1` is a Windows launcher by definition and can never be a
  // Claude Code executable, so failing to read one is a hard stop rather than
  // "carry on and patch this file". An extensionless file that does not parse
  // usually IS the program (the native installer's `~/.local/bin/claude`).
  const unreadable: NpmShimReading = kind === 'sh'
    ? { kind: 'not-a-shim' }
    : { kind: 'unreadable-launcher' };

  const text = readLauncherText(shimPath);
  if (text === null) return unreadable;

  const rawTargets = collectTargets(text, kind);
  if (rawTargets.length === 0) return unreadable;
  // One unreadable target poisons the whole file: clodex cannot prove the
  // remaining ones are the program when it cannot read what the others name.
  if (!rawTargets.every(isLiteralPath)) return unreadable;

  // Batch launchers spell their path with backslashes; POSIX `path.resolve`
  // would treat those as ordinary filename characters, which is what makes the
  // real npm `.cmd` fixtures testable off Windows. `/` is a valid separator on
  // Windows, so this stays correct there too.
  const absolute = rawTargets.map(target => resolve(
    dirname(shimPath),
    kind === 'cmd' ? target.replace(/\\/g, '/') : target,
  ));
  const distinct = [...new Set(absolute)];
  // The real launchers repeat one target across their branches (PowerShell names
  // it up to four times), so repetition is normal and disagreement is not.
  if (distinct.length > 1) return { kind: 'ambiguous-launcher', targets: distinct };
  return { kind: 'target', target: distinct[0] as string };
}

export type NpmShimResolution =
  | { ok: true; path: string }
  | {
      ok: false;
      /** The launcher that could not be followed. */
      shimPath: string;
      /** The program it names, when exactly one could be read. */
      declaredTarget: string | null;
      /** Sentence fragment describing why, for the user-facing message. */
      detail: string;
    };

/**
 * Follow `startPath` through any npm launchers to the file that actually runs,
 * resolving symlinks (and Windows directory junctions, which `realpathSync`
 * also resolves) at every hop.
 *
 * Returns a failure rather than a best guess whenever the chain cannot be
 * followed, so the caller can stop BEFORE it copies, backs up or renames
 * anything.
 */
export function resolveThroughNpmShims(startPath: string): NpmShimResolution {
  let current = startPath;
  // One extra pass so the file reached after MAX_LAUNCHER_HOPS launchers is
  // still classified: the cap counts launchers followed, not files looked at.
  for (let hop = 0; hop <= MAX_LAUNCHER_HOPS; hop++) {
    const reading = readNpmShim(current);
    if (reading.kind === 'not-a-shim') return { ok: true, path: current };
    if (reading.kind === 'unreadable-launcher') {
      return {
        ok: false,
        shimPath: current,
        declaredTarget: null,
        detail: 'it is a launcher script rather than the program itself, and clodex could not read '
          + 'which file it starts',
      };
    }
    if (reading.kind === 'ambiguous-launcher') {
      return {
        ok: false,
        shimPath: current,
        declaredTarget: null,
        detail: `it names more than one program (${reading.targets.join(', ')}), so clodex cannot `
          + 'tell which one it starts',
      };
    }
    let hopped: string;
    try {
      hopped = realpathSync(reading.target);
    } catch {
      return {
        ok: false,
        shimPath: current,
        declaredTarget: reading.target,
        detail: `it starts ${reading.target}, which does not exist`,
      };
    }
    current = hopped;
  }
  return {
    ok: false,
    shimPath: startPath,
    declaredTarget: null,
    detail: `it starts another launcher script, more than ${MAX_LAUNCHER_HOPS} deep`,
  };
}
